import {
    ComparisonService,
    CompetitorAnalysis,
    CompetitorAnalysisComparison,
} from '../types/competitors';

const isFiniteNumber = (value: number) =>
    typeof value === 'number' && Number.isFinite(value);
const isNullableNumber = (value: number | null) =>
    value === null || isFiniteNumber(value);
const isNullableString = (value: string | null) =>
    value === null || typeof value === 'string';
const isAttributeDepthSummary = (
    value: CompetitorAnalysisComparison['ownAttributeDepth']
) =>
    value !== null &&
    typeof value === 'object' &&
    isFiniteNumber(value.applicableCount) &&
    isFiniteNumber(value.filledCount) &&
    isFiniteNumber(value.notEvaluatedCount) &&
    typeof value.isComplete === 'boolean' &&
    isFiniteNumber(value.score);

const isComparisonService = (service: ComparisonService) => {
    if (!service || typeof service !== 'object') {
        return false;
    }
    const depth = service.attributeDepth;
    return (
        typeof service.code === 'string' &&
        typeof service.name === 'string' &&
        typeof service.category === 'string' &&
        typeof service.ownHotel === 'boolean' &&
        typeof service.competitor === 'boolean' &&
        typeof service.evidence === 'string' &&
        depth !== null &&
        typeof depth === 'object' &&
        isFiniteNumber(depth.applicableCount) &&
        isNullableNumber(depth.ownFilledCount) &&
        isNullableNumber(depth.competitorConfirmedCount) &&
        isNullableNumber(depth.competitorNotFoundCount) &&
        isNullableNumber(depth.competitorNotEvaluatedCount) &&
        Array.isArray(depth.attributes) &&
        depth.attributes.every(
            attribute =>
                attribute !== null &&
                typeof attribute === 'object' &&
                isFiniteNumber(attribute.id) &&
                typeof attribute.name === 'string' &&
                (attribute.ownFilled === null ||
                    typeof attribute.ownFilled === 'boolean') &&
                typeof attribute.competitorStatus === 'string' &&
                isNullableString(attribute.evidence) &&
                isNullableString(attribute.sourceUrl)
        )
    );
};

/**
 * Validates all report fields before exposing downloaded JSON to the viewer.
 * @param report Parsed analysis file, whose declared shape must be checked at runtime.
 * @returns The validated comparison.
 * @throws When required fields, arrays or nested entries are malformed.
 */
export const validateCompetitorAnalysis = (
    report: CompetitorAnalysis
): CompetitorAnalysisComparison => {
    const comparison = report?.comparison;
    const competitor = comparison?.competitor;
    if (
        !comparison ||
        typeof comparison !== 'object' ||
        !competitor ||
        typeof competitor !== 'object' ||
        ![
            competitor.name,
            competitor.brand,
            competitor.city,
            competitor.distance,
            competitor.starRating,
            competitor.rationale,
            comparison.competitorName,
            comparison.recommendation,
        ].every(value => typeof value === 'string') ||
        ![
            comparison.catalogSize,
            comparison.ownServiceCount,
            comparison.competitorServiceCount,
            comparison.coverageScore,
        ].every(isFiniteNumber) ||
        !isAttributeDepthSummary(comparison.ownAttributeDepth) ||
        !isAttributeDepthSummary(comparison.competitorAttributeDepth) ||
        ![
            comparison.advantagesOverCompetitor,
            comparison.gapsVsCompetitor,
            comparison.matchedServices,
        ].every(
            services =>
                Array.isArray(services) && services.every(isComparisonService)
        )
    ) {
        throw new Error('The analysis file has an unexpected format.');
    }
    return comparison;
};
